import React, { useCallback, useEffect, useState } from 'react';
import {
  Badge,
  Box,
  Button,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  Menu,
  MenuItem,
  Select,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import {
  Add,
  Assignment,
  CheckCircle,
  Delete,
  Edit,
  MoreVert,
  People,
  Refresh,
  Search,
  Send,
  Warning,
} from '@mui/icons-material';
import { managerAPI } from '../../services/api';
import { EmptyState, Section, StatusChip, TableShell } from '../shared/ui';
import { tokens } from '../../theme';
import RequirementForm from './RequirementForm';

const STATUS_FILTERS = [
  { value: 'all', label: 'All' },
  { value: 'draft', label: 'Draft' },
  { value: 'open', label: 'Open' },
  { value: 'in_sourcing', label: 'In sourcing' },
  { value: 'fulfilled', label: 'Fulfilled' },
  { value: 'closed', label: 'Closed' },
  { value: 'cancelled', label: 'Cancelled' },
];

const formatBudget = (r) => {
  if (r.budget_min == null && r.budget_max == null) return '—';
  const symbols = { INR: '₹', USD: '$', EUR: '€', GBP: '£' };
  const symbol = symbols[r.budget_currency] || `${r.budget_currency} `;
  const fmt = (v) => `${symbol}${Number(v).toLocaleString()}`;
  if (r.budget_min != null && r.budget_max != null) return `${fmt(r.budget_min)} – ${fmt(r.budget_max)}`;
  return r.budget_max != null ? `Up to ${fmt(r.budget_max)}` : `From ${fmt(r.budget_min)}`;
};

/** The manager's own requirements, with progress and the actions each state allows. */
const RequirementList = ({ onViewCandidates, onChanged }) => {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [status, setStatus] = useState('all');
  const [search, setSearch] = useState('');
  const [formOpen, setFormOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [menu, setMenu] = useState({ anchor: null, row: null });
  const [busy, setBusy] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = { per_page: 100 };
      if (status !== 'all') params.status = status;
      if (search.trim()) params.q = search.trim();
      const response = await managerAPI.getRequirements(params);
      setRows(response.data?.requirements || []);
    } catch (e) {
      setError(e.response?.data?.error || 'Could not load requirements.');
    } finally {
      setLoading(false);
    }
  }, [status, search]);

  useEffect(() => {
    const timer = setTimeout(load, search ? 300 : 0);
    return () => clearTimeout(timer);
  }, [load, search]);

  const closeMenu = () => setMenu({ anchor: null, row: null });

  const act = async (row, action) => {
    closeMenu();
    setBusy(row.id);
    setError(null);
    try {
      if (action === 'publish') {
        await managerAPI.publishRequirement(row.id);
        setNotice(`${row.requirement_code} published to vendor/HR.`);
      } else if (action === 'delete') {
        await managerAPI.deleteRequirement(row.id);
        setNotice(`${row.requirement_code} deleted.`);
      } else {
        await managerAPI.closeRequirement(row.id, action);
        setNotice(`${row.requirement_code} marked ${action}.`);
      }
      await load();
      onChanged?.();
    } catch (e) {
      setError(e.response?.data?.error || 'That action could not be completed.');
    } finally {
      setBusy(null);
    }
  };

  return (
    <>
      <Section
        title={`My requirements (${rows.length})`}
        subtitle="Requests you have raised, and how sourcing is going against each."
        disablePadding
        actions={
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <TextField
              size="small"
              placeholder="Search…"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              sx={{ width: 200 }}
              InputProps={{
                startAdornment: <Search sx={{ mr: 1, fontSize: 18, color: tokens.color.inkTertiary }} />,
              }}
            />
            <FormControl size="small" sx={{ width: 140 }}>
              <Select value={status} onChange={(e) => setStatus(e.target.value)}>
                {STATUS_FILTERS.map((s) => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
            <Tooltip title="Refresh">
              <span>
                <IconButton size="small" onClick={load} disabled={loading}>
                  <Refresh fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
            <Button
              variant="contained"
              size="small"
              startIcon={<Add fontSize="small" />}
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
            >
              New requirement
            </Button>
          </Box>
        }
      >
        {(error || notice) && (
          <Box sx={{ px: 2, pt: 2 }}>
            <Typography variant="body2" sx={{ color: error ? tokens.color.danger : tokens.color.success }}>
              {error || notice}
            </Typography>
          </Box>
        )}

        {loading && rows.length === 0 ? (
          <Box sx={{ display: 'grid', placeItems: 'center', py: 6 }}>
            <CircularProgress size={24} />
          </Box>
        ) : rows.length === 0 ? (
          <EmptyState
            icon={<Assignment />}
            title={status === 'all' ? 'No requirements yet' : `No ${status.replace('_', ' ')} requirements`}
            description="Raise a requirement describing the expertise and experience you need, and it will go to vendor/HR to source."
            action={
              <Button
                variant="contained"
                startIcon={<Add fontSize="small" />}
                onClick={() => {
                  setEditing(null);
                  setFormOpen(true);
                }}
              >
                New requirement
              </Button>
            }
          />
        ) : (
          <TableShell sx={{ border: 'none' }}>
            <Table size="small" sx={{ minWidth: 1140 }}>
              <TableHead>
                <TableRow>
                  <TableCell>Requirement</TableCell>
                  <TableCell>Expertise</TableCell>
                  <TableCell>Experience</TableCell>
                  <TableCell align="center">Openings</TableCell>
                  <TableCell>Location</TableCell>
                  <TableCell>Budget</TableCell>
                  <TableCell align="center">Priority</TableCell>
                  <TableCell align="center">Status</TableCell>
                  <TableCell align="center">Vendors</TableCell>
                  <TableCell align="center">Candidates</TableCell>
                  <TableCell align="right">Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => (
                  <TableRow key={r.id} hover>
                    <TableCell>
                      <Typography variant="subtitle1" noWrap>{r.role_title}</Typography>
                      <Typography variant="caption" sx={{ fontFamily: tokens.font.mono }}>
                        {r.requirement_code || 'draft'}
                      </Typography>
                      {r.target_onboarding_date && (
                        <Typography variant="body2" sx={{ color: tokens.color.inkSecondary }}>
                          by {new Date(r.target_onboarding_date).toLocaleDateString()}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell sx={{ maxWidth: 200 }}>
                      <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                        {(r.skills || []).slice(0, 3).map((s) => (
                          <Chip key={s} size="small" variant="outlined" label={s} />
                        ))}
                        {(r.skills || []).length > 3 && <Chip size="small" label={`+${r.skills.length - 3}`} />}
                      </Box>
                    </TableCell>
                    <TableCell>{r.experience_band}</TableCell>
                    <TableCell align="center">
                      {r.hired_count ? `${r.hired_count}/${r.headcount}` : r.headcount}
                    </TableCell>
                    <TableCell>{r.location}</TableCell>
                    <TableCell>{formatBudget(r)}</TableCell>
                    <TableCell align="center"><StatusChip status={r.priority} /></TableCell>
                    <TableCell align="center"><StatusChip status={r.status} /></TableCell>
                    <TableCell align="center">{r.vendor_count || 0}</TableCell>
                    <TableCell align="center">
                      <Tooltip
                        title={
                          r.duplicate_count
                            ? `${r.candidate_count} submitted, ${r.duplicate_count} flagged as duplicates`
                            : `${r.candidate_count || 0} submitted`
                        }
                      >
                        <Box
                          component="span"
                          onClick={() => r.candidate_count && onViewCandidates?.(r)}
                          sx={{
                            display: 'inline-flex',
                            alignItems: 'center',
                            gap: 0.5,
                            cursor: r.candidate_count ? 'pointer' : 'default',
                            color: r.candidate_count ? tokens.color.accent : tokens.color.inkTertiary,
                            fontWeight: 600,
                          }}
                        >
                          <Badge
                            color="warning"
                            badgeContent={r.duplicate_count || 0}
                            invisible={!r.duplicate_count}
                          >
                            <People sx={{ fontSize: 16 }} />
                          </Badge>
                          {r.candidate_count || 0}
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="right">
                      <Box sx={{ display: 'flex', gap: 0.25, justifyContent: 'flex-end' }}>
                        {r.status === 'draft' && (
                          <Tooltip title="Publish to vendor/HR">
                            <span>
                              <IconButton size="small" disabled={busy === r.id} onClick={() => act(r, 'publish')}>
                                <Send fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                        )}
                        <Tooltip title="Edit">
                          <span>
                            <IconButton
                              size="small"
                              disabled={['fulfilled', 'closed', 'cancelled'].includes(r.status)}
                              onClick={() => {
                                setEditing(r);
                                setFormOpen(true);
                              }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title="More">
                          <span>
                            <IconButton
                              size="small"
                              disabled={busy === r.id}
                              onClick={(e) => setMenu({ anchor: e.currentTarget, row: r })}
                            >
                              {busy === r.id ? <CircularProgress size={14} /> : <MoreVert fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableShell>
        )}
      </Section>

      <Menu anchorEl={menu.anchor} open={!!menu.anchor} onClose={closeMenu}>
        <MenuItem
          disabled={!menu.row?.candidate_count}
          onClick={() => {
            const row = menu.row;
            closeMenu();
            onViewCandidates?.(row);
          }}
        >
          <People fontSize="small" style={{ marginRight: 8 }} />
          View candidates
        </MenuItem>
        {menu.row && !['fulfilled', 'closed', 'cancelled'].includes(menu.row.status) && (
          <MenuItem onClick={() => act(menu.row, 'fulfilled')}>
            <CheckCircle fontSize="small" style={{ marginRight: 8 }} />
            Mark fulfilled
          </MenuItem>
        )}
        {menu.row && !['closed', 'cancelled'].includes(menu.row.status) && (
          <MenuItem onClick={() => act(menu.row, 'closed')}>
            <Warning fontSize="small" style={{ marginRight: 8 }} />
            Close
          </MenuItem>
        )}
        {menu.row && !['cancelled'].includes(menu.row.status) && (
          <MenuItem onClick={() => act(menu.row, 'cancelled')} sx={{ color: tokens.color.danger }}>
            <Warning fontSize="small" style={{ marginRight: 8 }} />
            Cancel
          </MenuItem>
        )}
        {menu.row?.status === 'draft' && !menu.row?.job_id && (
          <MenuItem onClick={() => act(menu.row, 'delete')} sx={{ color: tokens.color.danger }}>
            <Delete fontSize="small" style={{ marginRight: 8 }} />
            Delete draft
          </MenuItem>
        )}
      </Menu>

      <RequirementForm
        open={formOpen}
        requirement={editing}
        onClose={() => setFormOpen(false)}
        onSaved={(result) => {
          setNotice(result?.message || 'Requirement saved.');
          load();
          onChanged?.();
        }}
      />
    </>
  );
};

export default RequirementList;
